import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useApi } from '@/services/api';
import { IssueRelations } from './IssueRelations';
import {
  X,
  Maximize2,
  Minimize2,
  Calendar,
  Flag,
  Tag,
  User,
  MessageSquare,
  Clock,
  Link2,
  ChevronRight,
  RefreshCw,
  Boxes,
  Paperclip,
  Upload,
  FileText,
  Image,
  File,
  Trash2,
  Zap,
  Target,
  GitBranch,
  Plus,
  History,
  ArrowRight,
} from 'lucide-react';
import clsx from 'clsx';

interface IssuePeekOverviewProps {
  issueId: string | null;
  projectId: string;
  onClose: () => void;
}

const PRIORITY_OPTIONS = [
  { value: 1, label: 'None', color: 'bg-gray-400' },
  { value: 2, label: 'Low', color: 'bg-green-500' },
  { value: 3, label: 'Medium', color: 'bg-yellow-500' },
  { value: 4, label: 'High', color: 'bg-orange-500' },
  { value: 5, label: 'Urgent', color: 'bg-red-500' },
];

// Helper to format ISO date to yyyy-MM-dd for HTML date inputs
function formatDateForInput(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  // Handle both ISO format (2026-01-26T00:00:00Z) and already formatted (2026-01-26)
  return isoDate.split('T')[0];
}

export function IssuePeekOverview({ issueId, projectId, onClose }: IssuePeekOverviewProps) {
  const { workspaceId } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [newComment, setNewComment] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId),
    enabled: !!projectId,
  });

  const { data: labels } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => api.getLabels(projectId),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const { data: cycles } = useQuery({
    queryKey: ['cycles', projectId],
    queryFn: () => api.getCycles(projectId),
    enabled: !!projectId,
  });

  const { data: modules } = useQuery({
    queryKey: ['modules', projectId],
    queryFn: () => api.getModules(projectId),
    enabled: !!projectId,
  });

  const { data: epics } = useQuery({
    queryKey: ['epics', projectId],
    queryFn: () => api.getEpics(projectId),
    enabled: !!projectId,
  });

  const { data: subIssues } = useQuery({
    queryKey: ['subIssues', issueId],
    queryFn: () => api.getSubIssues(issueId!),
    enabled: !!issueId,
  });

  const { data: activities } = useQuery({
    queryKey: ['activities', issueId],
    queryFn: () => api.getActivities(issueId!),
    enabled: !!issueId,
  });

  const { data: comments, isLoading: commentsLoading } = useQuery({
    queryKey: ['comments', issueId],
    queryFn: () => api.getComments(issueId!),
    enabled: !!issueId,
  });

  const { data: attachments } = useQuery({
    queryKey: ['attachments', issueId],
    queryFn: () => api.getFiles(issueId!),
    enabled: !!issueId,
  });

  const issue = workItems?.find((wi: any) => wi.bh_workitemid === issueId);

  useEffect(() => {
    if (issue) {
      setTitle(issue.bh_name || '');
      setDescription(issue.bh_description || '');
    }
  }, [issue]);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.updateWorkItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
    },
  });

  const createCommentMutation = useMutation({
    mutationFn: (data: any) => api.createComment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', issueId] });
      setNewComment('');
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', issueId] });
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: (file: File) => api.uploadFile(file, issueId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', issueId] });
      setIsUploadingFile(false);
    },
    onError: () => {
      setIsUploadingFile(false);
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: string) => api.deleteFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', issueId] });
    },
  });

  const handleUpdate = (field: string, value: any) => {
    if (!issueId) return;
    updateMutation.mutate({
      id: issueId,
      data: { [field]: value },
    });
  };

  const handleTitleSave = () => {
    if (title.trim() && title !== issue?.bh_name) {
      handleUpdate('bh_name', title.trim());
    }
    setEditingTitle(false);
  };

  const handleDescriptionSave = () => {
    if (description !== issue?.bh_description) {
      handleUpdate('bh_description', description);
    }
    setEditingDescription(false);
  };

  const getStateById = (stateId: string) => {
    return states?.find((s: any) => s.bh_stateid === stateId);
  };

  const getMemberById = (memberId: string) => members?.find((m: any) => m.bh_memberid === memberId);

  const getActivityDescription = (activity: any) => {
    const actor = getMemberById(activity.bh_actor);
    switch (activity.bh_action) {
      case 'created':
        return `${actor?.bh_name || 'Someone'} created this issue`;
      case 'updated':
        if (activity.bh_field === 'state') {
          const oldState = getStateById(activity.bh_oldvalue);
          const newState = getStateById(activity.bh_newvalue);
          return `${actor?.bh_name || 'Someone'} changed status from ${oldState?.bh_name || 'Unknown'} to ${newState?.bh_name || 'Unknown'}`;
        }
        if (activity.bh_field === 'priority') {
          const oldPriority = PRIORITY_OPTIONS.find(p => p.value === parseInt(activity.bh_oldvalue));
          const newPriority = PRIORITY_OPTIONS.find(p => p.value === parseInt(activity.bh_newvalue));
          return `${actor?.bh_name || 'Someone'} changed priority from ${oldPriority?.label || 'None'} to ${newPriority?.label || 'None'}`;
        }
        if (activity.bh_field === 'assignee') {
          const newAssignee = getMemberById(activity.bh_newvalue);
          return `${actor?.bh_name || 'Someone'} assigned to ${newAssignee?.bh_name || 'Unassigned'}`;
        }
        return `${actor?.bh_name || 'Someone'} updated ${activity.bh_field}`;
      case 'comment':
        return `${actor?.bh_name || 'Someone'} added a comment`;
      default:
        return `${actor?.bh_name || 'Someone'} ${activity.bh_action}`;
    }
  };

  const handleAddComment = () => {
    if (!newComment.trim() || !issueId) return;
    createCommentMutation.mutate({
      bh_content: newComment.trim(),
      bh_workitem: issueId,
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingFile(true);
      uploadFileMutation.mutate(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimetype: string) => {
    if (mimetype?.startsWith('image/')) return Image;
    if (mimetype?.includes('pdf')) return FileText;
    return File;
  };

  if (!issueId || !issue) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={clsx(
          'fixed top-0 right-0 h-full bg-white shadow-2xl z-50 flex flex-col transition-all duration-300',
          isFullscreen ? 'w-full' : 'w-[600px] max-w-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-gray-500">
              #{issue.bh_sequenceid}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className={clsx('flex', isFullscreen ? 'flex-row' : 'flex-col')}>
            {/* Main Content */}
            <div className={clsx('flex-1 p-6', isFullscreen && 'border-r border-gray-200')}>
              {/* Title */}
              {editingTitle ? (
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave();
                    if (e.key === 'Escape') {
                      setTitle(issue.bh_name);
                      setEditingTitle(false);
                    }
                  }}
                  className="w-full text-xl font-semibold text-gray-900 border-b-2 border-primary-500 focus:outline-none pb-1"
                  autoFocus
                />
              ) : (
                <h2
                  onClick={() => setEditingTitle(true)}
                  className="text-xl font-semibold text-gray-900 cursor-text hover:bg-gray-50 rounded px-1 -mx-1"
                >
                  {issue.bh_name}
                </h2>
              )}

              {/* Description */}
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                {editingDescription ? (
                  <div>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      placeholder="Add a description..."
                      autoFocus
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={handleDescriptionSave}
                        className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setDescription(issue.bh_description || '');
                          setEditingDescription(false);
                        }}
                        className="px-3 py-1.5 text-gray-600 text-sm hover:bg-gray-100 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => setEditingDescription(true)}
                    className="min-h-[80px] p-3 bg-gray-50 rounded-lg cursor-text hover:bg-gray-100 transition-colors"
                  >
                    {issue.bh_description ? (
                      <p className="text-gray-700 whitespace-pre-wrap">{issue.bh_description}</p>
                    ) : (
                      <p className="text-gray-400">Click to add a description...</p>
                    )}
                  </div>
                )}
              </div>

              {/* Relations Section */}
              <IssueRelations
                issueId={issueId}
                projectId={projectId}
                onNavigateToIssue={(newIssueId) => {
                  // Could implement navigation to another issue here
                  // For now, we'll just log it
                  console.log('Navigate to issue:', newIssueId);
                }}
              />

              {/* Sub-Issues Section */}
              <div className="mt-8 border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <GitBranch className="w-4 h-4" />
                    Sub-Issues
                    {subIssues && subIssues.length > 0 && (
                      <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                        {subIssues.length}
                      </span>
                    )}
                  </h3>
                </div>

                {subIssues && subIssues.length > 0 ? (
                  <div className="space-y-2">
                    {subIssues.map((subIssue: any) => {
                      const subState = getStateById(subIssue._bh_state_value);
                      return (
                        <div
                          key={subIssue.bh_workitemid}
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: subState?.bh_color || '#6b7280' }}
                          />
                          <span className="text-sm font-mono text-gray-500">
                            #{subIssue.bh_sequenceid}
                          </span>
                          <span className="text-sm text-gray-900 truncate flex-1">
                            {subIssue.bh_name}
                          </span>
                          {subIssue.bh_estimate && (
                            <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                              {subIssue.bh_estimate} pts
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
                    <GitBranch className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No sub-issues</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Create issues with this as parent to track sub-tasks
                    </p>
                  </div>
                )}
              </div>

              {/* Attachments Section */}
              <div className="mt-8 border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Attachments
                    {attachments && attachments.length > 0 && (
                      <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                        {attachments.length}
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingFile}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <Upload className="w-4 h-4" />
                    Upload
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {isUploadingFile && (
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    Uploading...
                  </div>
                )}

                {attachments && attachments.length > 0 ? (
                  <div className="space-y-2">
                    {attachments.map((file: any) => {
                      const FileIcon = getFileIcon(file.bh_mimetype);
                      return (
                        <div
                          key={file.bh_attachmentid}
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg group"
                        >
                          <div className="w-8 h-8 bg-white rounded flex items-center justify-center border border-gray-200">
                            <FileIcon className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">{file.bh_filename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.bh_filesize)}</p>
                          </div>
                          <button
                            onClick={() => deleteFileMutation.mutate(file.bh_attachmentid)}
                            className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
                    <Paperclip className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No attachments yet</p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-primary-600 hover:text-primary-700 mt-1"
                    >
                      Upload a file
                    </button>
                  </div>
                )}
              </div>

              {/* Activity Section */}
              <div className="mt-8 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comments
                  {comments && comments.length > 0 && (
                    <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                      {comments.length}
                    </span>
                  )}
                </h3>

                {/* Comment Input */}
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-sm"
                      rows={2}
                    />
                    {newComment && (
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={handleAddComment}
                          disabled={createCommentMutation.isPending}
                          className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                          {createCommentMutation.isPending ? 'Posting...' : 'Comment'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comments List */}
                {commentsLoading ? (
                  <div className="py-4 text-center text-gray-500 text-sm">Loading comments...</div>
                ) : comments && comments.length > 0 ? (
                  <div className="space-y-4">
                    {comments.map((comment: any) => {
                      const author = getMemberById(comment.bh_author);
                      return (
                        <div key={comment.bh_commentid} className="flex gap-3 group">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {author?.bh_name || 'Unknown User'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(comment.createdon).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              <button
                                onClick={() => deleteCommentMutation.mutate(comment.bh_commentid)}
                                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {comment.bh_content}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {/* Created timestamp */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex gap-3 text-sm">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex-1 py-1">
                      <p className="text-gray-600">
                        Created on{' '}
                        <span className="font-medium text-gray-900">
                          {new Date(issue.createdon).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Log Section */}
              <div className="mt-8 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Activity Log
                  {activities && activities.length > 0 && (
                    <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                      {activities.length}
                    </span>
                  )}
                </h3>

                {activities && activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.slice(0, 10).map((activity: any) => (
                      <div key={activity.bh_activityid} className="flex gap-3 text-sm">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          {activity.bh_action === 'created' && <Plus className="w-3 h-3 text-green-500" />}
                          {activity.bh_action === 'updated' && <ArrowRight className="w-3 h-3 text-blue-500" />}
                          {activity.bh_action === 'comment' && <MessageSquare className="w-3 h-3 text-purple-500" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-600">{getActivityDescription(activity)}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(activity.createdon).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No activity recorded yet</p>
                )}
              </div>
            </div>

            {/* Properties Sidebar */}
            <div className={clsx('bg-gray-50 p-4', isFullscreen ? 'w-80' : 'border-t border-gray-200')}>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Properties
              </h3>

              <div className="space-y-4">
                {/* Assignee */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Assignee
                  </span>
                  <select
                    value={issue.bh_assignee || ''}
                    onChange={(e) => handleUpdate('bh_assignee', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[140px]"
                  >
                    <option value="">Unassigned</option>
                    {members?.map((member: any) => (
                      <option key={member.bh_memberid} value={member.bh_memberid}>
                        {member.bh_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* State */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <ChevronRight className="w-4 h-4" />
                    State
                  </span>
                  <select
                    value={issue._bh_state_value}
                    onChange={(e) => handleUpdate('bh_state', e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {states?.map((state: any) => (
                      <option key={state.bh_stateid} value={state.bh_stateid}>
                        {state.bh_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Flag className="w-4 h-4" />
                    Priority
                  </span>
                  <select
                    value={issue.bh_priority || 1}
                    onChange={(e) => handleUpdate('bh_priority', parseInt(e.target.value))}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Cycle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Cycle
                  </span>
                  <select
                    value={issue.bh_cycle || ''}
                    onChange={(e) => handleUpdate('bh_cycle', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[140px]"
                  >
                    <option value="">No cycle</option>
                    {cycles?.map((cycle: any) => (
                      <option key={cycle.bh_cycleid} value={cycle.bh_cycleid}>
                        {cycle.bh_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Module */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Boxes className="w-4 h-4" />
                    Module
                  </span>
                  <select
                    value={issue.bh_module || ''}
                    onChange={(e) => handleUpdate('bh_module', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[140px]"
                  >
                    <option value="">No module</option>
                    {modules?.map((module: any) => (
                      <option key={module.bh_moduleid} value={module.bh_moduleid}>
                        {module.bh_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Epic */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Epic
                  </span>
                  <select
                    value={issue.bh_epic || ''}
                    onChange={(e) => handleUpdate('bh_epic', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[140px]"
                  >
                    <option value="">No epic</option>
                    {epics?.map((epic: any) => (
                      <option key={epic.bh_epicid} value={epic.bh_epicid}>
                        {epic.bh_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Parent Issue */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <GitBranch className="w-4 h-4" />
                    Parent
                  </span>
                  <select
                    value={issue.bh_parent || ''}
                    onChange={(e) => handleUpdate('bh_parent', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[140px]"
                  >
                    <option value="">No parent</option>
                    {workItems?.filter((wi: any) => wi.bh_workitemid !== issueId && !wi.bh_parent).map((wi: any) => (
                      <option key={wi.bh_workitemid} value={wi.bh_workitemid}>
                        #{wi.bh_sequenceid} {wi.bh_name.substring(0, 20)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Estimate */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Estimate
                  </span>
                  <select
                    value={issue.bh_estimate || ''}
                    onChange={(e) => handleUpdate('bh_estimate', e.target.value ? parseInt(e.target.value) : null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">None</option>
                    <option value="1">1 point</option>
                    <option value="2">2 points</option>
                    <option value="3">3 points</option>
                    <option value="5">5 points</option>
                    <option value="8">8 points</option>
                    <option value="13">13 points</option>
                    <option value="21">21 points</option>
                  </select>
                </div>

                {/* Start Date */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Start Date
                  </span>
                  <input
                    type="date"
                    value={formatDateForInput(issue.bh_startdate)}
                    onChange={(e) => handleUpdate('bh_startdate', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Due Date */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Due Date
                  </span>
                  <input
                    type="date"
                    value={formatDateForInput(issue.bh_targetdate)}
                    onChange={(e) => handleUpdate('bh_targetdate', e.target.value || null)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Labels */}
                <div>
                  <span className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4" />
                    Labels
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {labels?.map((label: any) => (
                      <button
                        key={label.bh_labelid}
                        className="px-2 py-0.5 rounded-full text-xs font-medium text-white opacity-50 hover:opacity-100 transition-opacity"
                        style={{ backgroundColor: label.bh_color }}
                      >
                        {label.bh_name}
                      </button>
                    ))}
                    {(!labels || labels.length === 0) && (
                      <span className="text-xs text-gray-400">No labels available</span>
                    )}
                  </div>
                </div>

                {/* Links */}
                <div className="pt-4 border-t border-gray-200">
                  <button className="text-sm text-gray-600 flex items-center gap-2 hover:text-primary-600">
                    <Link2 className="w-4 h-4" />
                    Add link
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
